import { useState, useEffect, useRef, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface MediaItem {
  url: string;
  type: "image" | "video";
}

interface ServiceGalleryProps {
  mediaItems: MediaItem[];
  isOpen: boolean;
  onClose: () => void;
  initialIndex?: number;
}

export function ServiceGallery({ mediaItems, isOpen, onClose, initialIndex = 0 }: ServiceGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadedMedia, setLoadedMedia] = useState<Set<number>>(new Set());
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const videoRefs = useRef<Map<number, HTMLVideoElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset to initial index when dialog opens
  useEffect(() => {
    if (isOpen) {
      const safeIndex = Math.min(initialIndex, mediaItems.length - 1);
      setCurrentIndex(safeIndex);
      setLoadedMedia(new Set([safeIndex])); // Preload initial item
      // Preload adjacent items
      if (mediaItems.length > 1) {
        const prevIndex = safeIndex > 0 ? safeIndex - 1 : mediaItems.length - 1;
        const nextIndex = safeIndex < mediaItems.length - 1 ? safeIndex + 1 : 0;
        setLoadedMedia(new Set([safeIndex, prevIndex, nextIndex]));
      }
    }
  }, [isOpen, initialIndex, mediaItems.length]);

  // Lazy load media when it becomes visible or adjacent
  useEffect(() => {
    if (!isOpen || mediaItems.length === 0) return;

    const indicesToLoad = new Set<number>([currentIndex]);
    
    // Load previous and next items for smooth transitions
    if (mediaItems.length > 1) {
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : mediaItems.length - 1;
      const nextIndex = currentIndex < mediaItems.length - 1 ? currentIndex + 1 : 0;
      indicesToLoad.add(prevIndex);
      indicesToLoad.add(nextIndex);
    }

    setLoadedMedia((prev) => {
      const next = new Set(prev);
      indicesToLoad.forEach((idx) => next.add(idx));
      return next;
    });
  }, [isOpen, currentIndex, mediaItems.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goToNext();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Pause all videos except current
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (video && index !== currentIndex) {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [currentIndex]);

  const goToPrevious = useCallback(() => {
    if (isTransitioning || mediaItems.length === 0) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : mediaItems.length - 1));
    setTimeout(() => setIsTransitioning(false), 300);
  }, [isTransitioning, mediaItems.length]);

  const goToNext = useCallback(() => {
    if (isTransitioning || mediaItems.length === 0) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev < mediaItems.length - 1 ? prev + 1 : 0));
    setTimeout(() => setIsTransitioning(false), 300);
  }, [isTransitioning, mediaItems.length]);

  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current || isTransitioning) return;

    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      goToNext();
    } else if (distance < -minSwipeDistance) {
      goToPrevious();
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  if (mediaItems.length === 0) return null;

  const currentMedia = mediaItems[currentIndex];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-6xl w-full h-[95vh] p-0 gap-0 overflow-hidden bg-transparent border-0 shadow-none rounded-[16px]"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Close Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4 z-50 text-white hover:bg-white/10 rounded-full h-10 w-10"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </Button>

        {/* Navigation Arrows */}
        {mediaItems.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/10 rounded-full h-12 w-12 backdrop-blur-sm"
              onClick={goToPrevious}
              disabled={isTransitioning}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/10 rounded-full h-12 w-12 backdrop-blur-sm"
              onClick={goToNext}
              disabled={isTransitioning}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </>
        )}

        {/* Media Display Container */}
        <div
          ref={containerRef}
          className="relative w-full h-full flex items-center justify-center overflow-hidden rounded-[16px] bg-black"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Render current and adjacent items for smooth transitions */}
          {mediaItems.map((item, index) => {
            const isActive = index === currentIndex;
            const isLoaded = loadedMedia.has(index);
            const offset = index - currentIndex;
            
            // Only render items that are visible or adjacent (for preloading)
            const shouldRender = Math.abs(offset) <= 1 || 
                                 (offset === mediaItems.length - 1 && currentIndex === 0) ||
                                 (offset === -(mediaItems.length - 1) && currentIndex === mediaItems.length - 1);

            if (!shouldRender) return null;

            return (
              <div
                key={`${index}-${currentIndex}`} // Force re-render on index change for smooth transitions
                className={cn(
                  "absolute inset-0 flex items-center justify-center transition-all duration-300 ease-out",
                  isActive ? "z-10 opacity-100 scale-100" : "z-0 opacity-0 scale-95 pointer-events-none"
                )}
              >
                 {isLoaded && (
                   <>
                     {item.type === "video" ? (
                       <video
                         ref={(el) => {
                           if (el) {
                             videoRefs.current.set(index, el);
                           } else {
                             videoRefs.current.delete(index);
                           }
                         }}
                         src={item.url}
                         className="w-full h-full object-cover rounded-[16px]"
                         controls={isActive}
                         autoPlay={isActive}
                         loop
                         muted={!isActive}
                         playsInline
                         preload={isActive ? "auto" : "none"}
                         onLoadedData={() => {
                           if (isActive && videoRefs.current.get(index)) {
                             videoRefs.current.get(index)?.play().catch(() => {
                               // Auto-play might fail, that's okay
                             });
                           }
                         }}
                       />
                     ) : (
                       <img
                         src={item.url}
                         alt={`Gallery item ${index + 1}`}
                         className="w-full h-full object-cover rounded-[16px]"
                         loading={isActive ? "eager" : "lazy"}
                       />
                     )}
                   </>
                 )}
              </div>
            );
          })}
        </div>

        {/* Thumbnail Strip */}
        {mediaItems.length > 1 && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-4 pb-6">
            <div className="flex gap-2 justify-center overflow-x-auto scrollbar-hide">
              {mediaItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (!isTransitioning) {
                      setIsTransitioning(true);
                      setCurrentIndex(index);
                      setTimeout(() => setIsTransitioning(false), 300);
                    }
                  }}
                  className={cn(
                    "flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 transition-all duration-200",
                    currentIndex === index
                      ? "border-white scale-110 shadow-lg"
                      : "border-white/30 opacity-70 hover:opacity-100 hover:scale-105"
                  )}
                >
                  {item.type === "video" ? (
                    <div className="relative w-full h-full bg-muted">
                      {loadedMedia.has(index) ? (
                        <video
                          src={item.url}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                          <div className="w-3 h-3 border-2 border-white/50 rounded-full" />
                        </div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <div className="w-3 h-3 border-2 border-white/80 rounded-full" />
                      </div>
                    </div>
                  ) : (
                    <img
                      src={item.url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Counter */}
        {mediaItems.length > 1 && (
          <div className="absolute top-4 left-4 z-50 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-medium">
            {currentIndex + 1} / {mediaItems.length}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
